import { BaseInitClip } from '@goldcast/player-config/src/classes/BaseInitClip';
import { ClipAvailabilityStatus } from '@goldcast/player-config/src/types/clip-availability';
import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { BrandKitStore, PlatformBrandKitStore } from '@goldcast/player-config/src/types/brand-kit-types';
import { updateClip } from '@goldcast/player-config/src/retrievers/updateClip';
import { getAllCompositeSegments } from '@goldcast/player-config/src/utils/clipUtils';
import { updateClipAvailabilityStatus } from './clipAvailabilityStatus';
import { fullTranscriptStore } from './fullTranscriptStore';
import { allClips, currentClip, logMigrationEvent } from './clip';
import { brandKit, clipsBrandKit, platformBrandKitStore } from './brandKit';
import { initHistory } from './clipHistory';
import { addPendingUpdate } from './pendingEditsStore';
import { infiniteSleep } from '@/libs/utils';
import { getVideoDurationFromSrc } from '@/libs/getVideoDuration';
import { showErrorToast, showInfoToast } from '@/libs/toast/toast';
import { zoomLevelStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/zoomLevel';
import { updateCaptions } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/captionUtils';
import { addPauseEntriesToParagraphs, buildPauseDeletesMap } from '@/components/molecules/Transcript/pauseUtils';
import { TranscriptParagraph } from '@/components/molecules/Transcript/types';

export class ContentLabInitClip extends BaseInitClip {
  protected async onAvailabilityStatus(status: ClipAvailabilityStatus) {
    updateClipAvailabilityStatus(this.props.clipId, status);
    if (status !== ClipAvailabilityStatus.ALL_OK) {
      await infiniteSleep();
    }
  }
  protected async fetchVideoDuration(url: string): Promise<number> {
    return getVideoDurationFromSrc(url);
  }
  protected getAuthHeader(): string {
    return `Bearer ${window.accessToken}`;
  }
  protected onSpeakerAnalsysisFetchFail(error) {
    window.logger?.error('Error fetching speakers analysis', error);
  }
  protected onErrorToast(content: string): void {
    showErrorToast(content);
  }
  protected onFullTranscript(transcript: Word[]): void {
    fullTranscriptStore.update(() => transcript);
  }
  protected logEvent(clip: Clip, name: string): void {
    logMigrationEvent(clip, name);
  }
  protected infoToast(content: string): void {
    showInfoToast(content);
  }
  protected getBrandkitValues(): {
    clipBrandKitStore: BrandKitStore;
    brandKitStore: BrandKitStore;
    platformBrandKit: PlatformBrandKitStore;
  } {
    return {
      clipBrandKitStore: clipsBrandKit.getSnapshot()[this.props.clipId],
      brandKitStore: brandKit.getSnapshot(),
      platformBrandKit: platformBrandKitStore.getSnapshot()
    };
  }
  protected getZoomLevel(): number {
    return zoomLevelStore.getSnapshot().zoomLevel;
  }
  protected getPreviousClip(): Clip {
    return currentClip.getSnapshot()[this.props.clipId];
  }
  protected updateCurrentClipStore(clip: Clip): void {
    currentClip.update(data => {
      return {
        ...data,
        [clip.id]: clip
      };
    });
  }
  protected initializeHistory(clip: Clip): void {
    initHistory(clip.id, clip.asset_metadata);
  }
  protected updateAllClipStore(clip: Clip): void {
    allClips.update(allClipsData => allClipsData.map(currClip => (currClip.id === clip.id ? clip : currClip)));
  }
  protected async persistClip({ clip, version }: { clip: Clip; version: number }): Promise<void> {
    const updatePromise = updateClip(clip);
    addPendingUpdate({ clipId: this.props.clipId, updatePromise, version });
    updatePromise.then(({ updated_at }) => {
      currentClip.update(data => {
        return {
          ...data,
          [this.props.clipId]: { ...data[this.props.clipId], updated_at }
        };
      });
    });
  }

  protected deriveReelCaptions(clip: Clip): void {
    try {
      updateCaptions(clip);
    } catch (error) {
      window.logger?.error('Highlight reel: failed to derive captions', error);
      return;
    }
    try {
      this.applyDefaultReelPauseRemoval(clip);
    } catch (error) {
      window.logger?.error('Highlight reel: failed to apply default pause removal', error);
    }
  }

  /**
   * Remove pauses by default on a freshly-seeded reel. For each composite segment we detect pauses
   * from its captions (source-time word timings) and merge the resulting PAUSE_OPTIMIZATION deletes
   * into `segment_metadata.deletes` — alongside the structural head/tail trims — before config-gen
   * builds playRanges. "Trim excess only": `getRecommendedPauseDuration` keeps a natural residual
   * gap and deletes only the surplus (min detected gap 0.4s). Runs unconditionally for reels; these
   * are ordinary PAUSE_OPTIMIZATION deletes, so the existing Restore/Restore-all UX undoes them.
   */
  private applyDefaultReelPauseRemoval(clip: Clip): void {
    const compositeSegments = getAllCompositeSegments(clip);
    for (const segment of compositeSegments) {
      const meta = segment.segment_metadata;
      const words = meta?.captions;
      if (!meta || !words || words.length < 2) {
        continue;
      }
      const paragraph: TranscriptParagraph = {
        segmentId: segment.id,
        segmentStartTime: meta.start,
        words
      };
      const [paragraphWithPauses] = addPauseEntriesToParagraphs([paragraph]);
      const entries = paragraphWithPauses.pauseEntries ?? [];
      if (!entries.length) {
        continue;
      }
      meta.deletes = { ...(meta.deletes ?? {}), ...buildPauseDeletesMap(entries) };
    }
  }
}
